import React, { type ReactChild, useCallback, type SyntheticEvent } from 'react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';
import { Show, Hide } from '@atlaskit/primitives/responsive';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIsCurrentBrowserSupported } from '@atlassian/jira-insights-common';
import {
	JIRA_INSIGHTS,
	INSIGHTS_BUTTON_SM,
	INSIGHTS_BUTTON_LG,
	INSIGHTS_BUTTON_RESPONSIVE,
} from '@atlassian/jira-insights-common-constants';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	FireOperationalAnalytics,
	FireUiAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useGlobalRefStoreActions } from '@atlassian/jira-refs-store';
import { ResponsiveButton } from '@atlassian/jira-responsive-button';
import messages from './messages';
import type { InsightsButtonProps, TooltipWrapperProps } from './types';

const actionSubjectId = 'toggleInsightsPanel';

const TooltipWrapper = ({ children, render }: TooltipWrapperProps) =>
	render != null ? render({ children }) : <>{children}</>;

const buttonTestId = 'insights-show-insights-button.ui.insights-button';
const buttonForHiddenTestId = 'insights-show-insights-button.ui.button-test-id-hide';

export const UnsafeInsightsButton = ({
	clickEventAnalytics,
	iconAfter,
	isCompact = false,
	isHidden = false,
	isInsightsPanelOpen,
	isOnboardingDisabled = false,
	onToggle,
	renderTooltip,
}: InsightsButtonProps) => {
	const { formatMessage } = useIntl();
	const [, { register }] = useGlobalRefStoreActions();

	const onRefSmall = useCallback(
		(ref: HTMLDivElement | null) => {
			register(INSIGHTS_BUTTON_SM, ref);
		},
		[register],
	);

	const onRefButtonLarge = useCallback(
		(ref: HTMLDivElement | null) => {
			register(INSIGHTS_BUTTON_LG, ref);
		},
		[register],
	);

	const onRefButtonResponsive = useCallback(
		(ref: HTMLDivElement | null) => {
			register(INSIGHTS_BUTTON_RESPONSIVE, ref);
		},
		[register],
	);

	const isSupported = useIsCurrentBrowserSupported();

	const isDisabled = isOnboardingDisabled;

	const onClick = useCallback(
		(_: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, actionSubjectId, {
				action: isInsightsPanelOpen ? 'close' : 'open',
				isDisabled,
				...(clickEventAnalytics?.attributes || {}),
			});

			onToggle(analyticsEvent);
		},
		[clickEventAnalytics, isDisabled, isInsightsPanelOpen, onToggle],
	);

	const label = isInsightsPanelOpen
		? formatMessage(messages.hideInsights)
		: formatMessage(messages.showInsights);

	if (!isSupported) {
		return null;
	}

	const showInsightsButton = !isHidden;

	if (!showInsightsButton) {
		return (
			<FireOperationalAnalytics
				action="hidden"
				actionSubject="button"
				actionSubjectId={actionSubjectId}
			/>
		);
	}

	const iconColor = isInsightsPanelOpen ? undefined : token('color.icon', colors.N500);

	return (
		<>
			<FireUiAnalytics action="rendered" actionSubject="button" actionSubjectId={actionSubjectId} />
			{ff('sea-2744-jsw-tabs-nav') ? (
				<ResponsiveButton
					interactionName="show-insights-button"
					icon={GraphLineIcon}
					label={label}
					iconAfter={typeof iconAfter === 'function' ? iconAfter : undefined}
					isSelected={isInsightsPanelOpen}
					onClick={onClick}
					testId={buttonTestId}
					ref={onRefButtonResponsive}
				/>
			) : (
				<TooltipWrapper render={renderTooltip}>
					{isCompact ? (
						<Button
							interactionName="show-insights-button"
							appearance="subtle"
							spacing="none"
							isSelected={isInsightsPanelOpen}
							iconBefore={<GraphLineIcon label={label} primaryColor={iconColor} />}
							onClick={onClick}
							testId={buttonTestId}
							ref={onRefSmall}
						/>
					) : (
						// eslint-disable-next-line jira/react/no-empty-divs
						<div>
							<Show above="lg">
								<Button
									interactionName="show-insights-button"
									isSelected={isInsightsPanelOpen}
									iconBefore={<GraphLineIcon label="" primaryColor={iconColor} />}
									iconAfter={
										typeof iconAfter === 'object'
											? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
												(iconAfter as ReactChild)
											: undefined
									}
									onClick={onClick}
									testId={buttonTestId}
									ref={onRefButtonLarge}
								>
									{label}
								</Button>
							</Show>
							{/* Render without text on smaller screen sizes */}
							<Hide above="lg">
								<Button
									interactionName="show-insights-button"
									isSelected={isInsightsPanelOpen}
									iconBefore={<GraphLineIcon label={label} primaryColor={iconColor} />}
									onClick={onClick}
									testId={buttonForHiddenTestId}
									ref={onRefSmall}
								/>
							</Hide>
						</div>
					)}
				</TooltipWrapper>
			)}
		</>
	);
};

export const SafeInsightsButton = (props: InsightsButtonProps) => (
	<JSErrorBoundary id="button" packageName={JIRA_INSIGHTS} fallback="flag">
		<UnsafeInsightsButton {...props} />
	</JSErrorBoundary>
);

export { SafeInsightsButton as InsightsButton };
